import { Options, Vue } from 'vue-class-component';
import { TrustAPI } from '@/shared/modules/trust-api';
import { Inject, Watch } from 'vue-property-decorator';
import { AppInsights } from '@/trust-admin-app/appInsights';
import { EventEmitter } from 'events';
import store from '@/trust-admin-app/store';
import { RouteN } from '@/trust-admin-app/constants/router-name';
import { FirmSearchRequest, NumberOfCompliantForms, NumberOfPracticeWithoutTrustCoordinator, NumberOfUnapprovedForm, PracticeView } from '@/shared/modules/typescript-trustapi-client/models';
import { debounce } from 'lodash';
import LoadingSpinner from '@/shared/components/loading-spinner/index.vue';
import moment from 'moment';

@Options({
  components: {
    LoadingSpinner
  }
})
export default class TrustAdminDashboard extends Vue {
  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private ai = new AppInsights;

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private loadingDataSet = true;
  private errorLoadingDataSet = false;

  private numberOfInvestigations = {};
  private numberOfInvestigationsPerInvestigator = [];

  private isOpen = false;
  private arrowCounter = -1;
  private practiceList: PracticeView[] = [];
  private search = '';
  private isSearching = false;
  private srchDebounced: any = null;
  private currentSelectedPractice: PracticeView = null as any;

  private totalNumberOfUnapprovedForms: NumberOfUnapprovedForm = {
    eeSignUpForms: 0,
    appointEeRequestForms: 0,
    terminateEeRequestForms: 0

  };
  private totalNumberOfPracticeWithoutTC: NumberOfPracticeWithoutTrustCoordinator = {
    practiceWithoutTrustCoordinator: 0,
    updatedOn: ''
  };
  private totalNumberOfComplaintForms: NumberOfCompliantForms = {
    nonCompliantPartA: 0,
    nonCompliantPartB: 0,
    nonCompliantEeReport: 0,
    partBWithoutExternalExaminer: 0,
    partBSubmittedNoExternalExaminer: 0
  };

  @Watch('search')
  private onKeywordChange(): void {
    if (this.search.length > 2 && this.practiceList.filter(x => x.name == this.search).length == 0) {
      this.currentSelectedPractice = null as any;
      setTimeout(this.srchDebounced.cancel, 0);
      setTimeout(this.srchDebounced, 0);
    }
  }

  @Watch('currentSelectedPractice')
  private onCurrentSelectedPracticeChange() {
    if (this.currentSelectedPractice.id) {
      this.$router.push({ name: RouteN.TA.Practice.n, query: { pId: this.currentSelectedPractice.id } });
    }
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.preloadData();
    window.addEventListener('click', this.handleClickOutside);
    this.srchDebounced = debounce(() => {
      this.searchPractice();
    }, 1000);
  }

  beforeUnmount(): void {
    window.removeEventListener('click', this.handleClickOutside);
  }
  private async preloadData(): Promise<void> {
    try {
      this.loadingDataSet = true;
      this.errorLoadingDataSet = false;
      const resp: any[] = await Promise.all([
        this.apiService.InvestigationApi?.apiV2InvestigationGetNumberOfInvestigationsGet(),
        this.apiService.InvestigationApi?.apiV2InvestigationGetNumerOfInvestigationsPerInvestigatorGet(),
        this.apiService.PeopleAtcsApi?.apiV2AtcsPeopleAdminDashboardGetNumberOfUnapprovedFormsGet(),
        this.apiService.PeopleAtcsApi?.apiV2AtcsPeopleAdminDashboardGetNumberOfPracticeWithoutTrustCoordinatorGet(),
        this.apiService.ComplianceFormApi?.apiV2ComplianceFormAdminDashboardGetNumberOfCompliantFormsGet()
      ]);
      this.numberOfInvestigations = resp[0].data.data;
      this.numberOfInvestigationsPerInvestigator = resp[1].data.data;
      this.totalNumberOfUnapprovedForms = resp[2].data;
      this.totalNumberOfPracticeWithoutTC = {
        practiceWithoutTrustCoordinator: resp[3].data.practiceWithoutTrustCoordinator,
        updatedOn: moment(resp[3].data.updatedOn).format('DD MMM YYYY, hh:mm A')
      };
      this.totalNumberOfComplaintForms = resp[4].data;
    } catch (err) {
      this.errorLoadingDataSet = true;
      this.ai.trackException('Error, Unable to preload data: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to preload data');
    } finally {
      this.loadingDataSet = false;
    }
  }

  private goToTrustTeam() {
    this.$router.push({ name: RouteN.TA.TrustDataTrustTeam.n });
  }

  private async searchPractice(): Promise<void> {
    const _body: FirmSearchRequest = {
      keyword: this.search,
      isExactMatch: false
    }
    try {
      this.isSearching = true;

      const resp: any = await this.apiService.PracticeApi?.apiV2PracticeSearchPracticePost(_body);
      this.practiceList = resp.data.data as PracticeView[];
      this.isOpen = this.practiceList.length > 0;
    } catch (err) {
      this.isOpen = false;
    } finally {
      this.isSearching = false;
    }
  }

  private selectPractice(p: PracticeView): void {
    this.isOpen = false;
    this.search = p.name;
    this.currentSelectedPractice = p;
  }

  private onArrowDown(evt: any) {
    if (this.arrowCounter < this.practiceList.length) {
      this.arrowCounter = this.arrowCounter + 1;
    }
  }

  private onArrowUp(): void {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
    }
  }

  private onEnter() {
    if (this.practiceList.length > 0) {
      this.selectPractice(
        this.practiceList[this.arrowCounter === -1 ? 0 : this.arrowCounter]
      );
    }
    this.isOpen = false;
  }

  private handleClickOutside(evt: any): void {
    if (this.$el === (evt.target)) {
      this.isOpen = false;
      this.arrowCounter = -1;
    }
  }

  private highlightWord(word: string): string {
    try {
      const regex = new RegExp('(' + this.search + ')', 'ig');
      return word.replace(regex, '<mark>$1</mark>');
    } catch (err) {
      return word;
    }
  }

  private formatPracticeText(p: PracticeView): string {
    if (p.branchId) {
      return p.firmId + ' - ' + p.branchId + ' | ' + p.name + ' - ' + p.tradingName;
    } else {
      return p.firmId + ' | ' + p.name;
    }
  }

}


